import apigClientFactory from '../chalice-javascript-sdk/apigClient';
import * as config from '../Config/config';
import * as SS from '../utility/secureStorage';
import { dropCookie } from '../utility/dropCookie';
import { readAccess } from '../utility/readAccess';
import { loadHubSpot } from '../utils/hubspot';
import { fetchUserCustomAttributes } from '../utils/tokenUtils';
import { jwtDecode } from 'jwt-decode';
import { Pause } from '@mui/icons-material';

export const decodeIdToken = () => {
    const idToken = SS.secureStorage_getItem("idtoken");
    if (!idToken) {
        return null;
    }
    try {
        return jwtDecode(idToken);
    } catch (error) {
        console.error('Error decoding ID token:', error);
        return null;
    }
};

// Default endpoints from config.js
const DEFAULT_US_PROD_ENDPOINTS = {
    apiurl: config.prodEndPoints['us-east-1'].apiurl,
    bucketurl: config.prodEndPoints['us-east-1'].bucketurl,
    bucketname: config.prodEndPoints['us-east-1'].bucketname
};

const DEFAULT_US_DEV_ENDPOINTS = {
    apiurl: config.devEndPoints['us-east-1'].apiurl,
    bucketurl: config.devEndPoints['us-east-1'].bucketurl,
    bucketname: config.devEndPoints['us-east-1'].bucketname
};

// Helper function to get default endpoints based on environment
const getDefaultEndpoints = (isProd = false) => {
    return isProd ? DEFAULT_US_PROD_ENDPOINTS : DEFAULT_US_DEV_ENDPOINTS;
};

// Helper function to ensure we have valid endpoints
const getValidEndpoints = (endpoints, region, isProd = false) => {
    const defaultEndpoints = getDefaultEndpoints(isProd);
    if (!endpoints || !endpoints[region]) {
        return defaultEndpoints;
    }
    const validEndpoints = {
        apiurl: endpoints[region].apiurl || defaultEndpoints.apiurl,
        bucketurl: endpoints[region].bucketurl || defaultEndpoints.bucketurl,
        bucketname: endpoints[region].bucketname || defaultEndpoints.bucketname
    };
    return validEndpoints;
};

function appendReverseKeyValuePairs(workflows) {
    let wft = { ...workflows };
    Object.keys(wft).forEach((key) => {
        workflows[wft[key]] = key;
    });
}

/**
 * Attempts to authenticate with each region until successful
 * @param {string} username - User's email or username
 * @param {string} password - User's password
 * @returns {Promise<Object>} Authentication result with tokens and user info
 */
export const getRegion = async (username, password) => {
    const currentConfig = config.createConfig();
    const isProd = currentConfig.isProd || true;
    const endPoints = isProd ? config.prodEndPoints : config.devEndPoints;
    const defaultEndpoints = getDefaultEndpoints(isProd);
    let result;

    // If no endpoints are available, use defaults
    if (!endPoints || Object.keys(endPoints).length === 0) {
        try {
            SS.secureStorage_setItem("apiurl", defaultEndpoints.apiurl);
            SS.secureStorage_setItem("bucketurl", defaultEndpoints.bucketurl);
            SS.secureStorage_setItem("bucketname", defaultEndpoints.bucketname);

            result = await getCredentials(username, password, defaultEndpoints);
            if (result) {
                return result;
            }
        } catch (error) {
            throw error;
        }
    }

    for (let region of Object.keys(endPoints)) {
        const regionEndpoint = getValidEndpoints(endPoints, region, isProd);

        SS.secureStorage_setItem("apiurl", regionEndpoint.apiurl);
        SS.secureStorage_setItem("bucketurl", regionEndpoint.bucketurl);
        SS.secureStorage_setItem("bucketname", regionEndpoint.bucketname);

        try {
            result = await getCredentials(username, password, regionEndpoint);
            if (result) {
                return result;
            }
        } catch (error) {
            if (JSON.stringify(error).includes("UnderReviewError")) {
                throw error;
            }
            if (!error.response) {
                continue;
            }
            if (error.response.data.Error && error.response.data.Error.includes('NotAuthorizedException')) {
                if (error.response.data.Region) {
                    const regionFromError = error.response.data.Region;
                    const validEndpoints = getValidEndpoints(endPoints, regionFromError, isProd);
                    SS.secureStorage_setItem("apiurl", validEndpoints.apiurl);
                    SS.secureStorage_setItem("bucketurl", validEndpoints.bucketurl);
                    SS.secureStorage_setItem("bucketname", validEndpoints.bucketname);
                }
                throw error;
            }
        }
    }

    throw new Error("Authentication failed in all regions");
};

/**
 * Authenticates user with provided credentials in a specific region
 * @param {string} username - User's email or username
 * @param {string} password - User's password
 * @param {Object} region - Region endpoint configuration
 * @returns {Promise<Object>} Authentication result with tokens and user info
 */
export const getCredentials = async (username, password, region) => {
    const apiurl = region.apiurl;
    const bucketurl = region.bucketurl;
    let apigClient = apigClientFactory.newClient(undefined, apiurl);
    const params = {
        username: username,
        password: password,
    };

    try {
        const result = await apigClient.vlauthSigninV2UsernamePasswordPut(params, '', '');
        // console.log('result', result);
        if ("Error" in result.data) {
            throw { name: "UnderReviewError", message: result.data.Error };
        }

        result.data.apiurl = apiurl;
        result.data.bucketurl = bucketurl;

        const currentConfig = config.createConfig();
        const isProd = currentConfig.isProd || true;
        const endPoints = isProd ? config.prodEndPoints : config.devEndPoints;

        if (result.data.region !== null) {
            const validEndpoints = getValidEndpoints(endPoints, result.data.region, isProd);
            SS.secureStorage_setItem("apiurl", validEndpoints.apiurl);
            SS.secureStorage_setItem("bucketurl", validEndpoints.bucketurl);
            SS.secureStorage_setItem("bucketname", validEndpoints.bucketname);
            apigClient = apigClientFactory.newClient(undefined, validEndpoints.apiurl);
        }

        const paramsTmp = {
            username: result.data.Username ? result.data.Username : username
        };

        const additionalParamsTmp = {
            headers: {
                "Authorization": result.data.IdToken
            }
        };

        console.log(paramsTmp);
        const userResponse = await apigClient.dbopsGetUserInfoUsernameGet(paramsTmp, '', additionalParamsTmp);

        if (userResponse.data.users.length === 0) {
            throw new Error("User doesn't exist");
        }

        const user = userResponse.data;
        result.data.S3Link = user.users[0].S3Link;
        result.data.AccountStatus = user.users[0].AccountStatus;
        result.data.workflows = user.workflows;
        result.data.secondary_customerIDs = user.users[0].SecondaryCustomerIDs === undefined ? "" : user.users[0].SecondaryCustomerIDs;
        //.data.CustomerIDKeys = user.users[0].CustomerIDKeys === undefined ? "" : user.users[0].CustomerIDKeys;

        const workflowValues = Object.values(result.data.workflows);
        const workflowCategories = workflowValues.map(workflow => {
            if (workflow.includes('PRD')) return 'product';
            if (workflow.includes('ORD')) return 'order';
            if (workflow.includes('INV')) return 'inventory';
            if (workflow.includes('FMT')) return 'fulfillment';
            if (workflow.includes('PRC')) return 'price';
            return 'unknown';
        });

        // SS.secureStorage_setItem('workflows', JSON.stringify(workflowCategories));
        SS.secureStorage_setItem('workflows names', JSON.stringify(workflowValues));

        return result;
    } catch (error) {
        throw error;
    }
};

/**
 * Simulates the original login flow using username and password
 * This function can be used to ensure all fields are populated as in the original login
 * @param {string} username - User's email or username
 * @param {string} password - User's password
 * @returns {Promise<boolean>} True if login was successful
 */
export const loginWithCredentials = async (username, password) => {
    try {
        // Clear any existing storage
        // This would typically be done by resetStorage() in the original flow
        const result = await getRegion(username, password);

        if ("Error" in result.data) {
            throw new Error(result.data.Error);
        }

        // Convert the result to the format expected by setupUserSession
        const tokens = {
            id_token: result.data.IdToken,
            access_token: result.data.AccessToken,
            refresh_token: result.data.refreshToken,
            expires_in: result.data.expiresIn
        };

        const userInfo = {
            CustomerID: result.data.CustomerID,
            Username: result.data.Username,
            email: username,
            region: result.data.region,
            secondary_customerIDs: result.data.secondary_customerIDs || '',
            workflows: result.data.workflows,
            AccountStatus: result.data.AccountStatus,
            S3Link: result.data.S3Link,
            status: 'active' // Default status
        };

        // Call setupUserSession with the converted data
        return await setupUserSession(tokens, userInfo);
    } catch (error) {
        throw error;
    }
};

export const setupUserSession = async (tokens, userInfo) => {
    try {
        const now = new Date().getTime();


        // Get config and default endpoints
        const currentConfig = config.createConfig();
        const isProd = currentConfig.isProd || true;
        const defaultEndpoints = getDefaultEndpoints(isProd);

        // Ensure all required fields exist with fallbacks
        userInfo = {
            CustomerID: '',
            Username: '',
            email: '',
            region: 'us-east-1',
            secondary_customerIDs: '',
            workflows: {},
            AccountStatus: '',
            S3Link: '',
            status: 'pending_review',
            ...userInfo // Override defaults with provided values
        };

        // If we have credentials but missing critical user info, try to get it via getRegion
        if (tokens.username && tokens.password && (!userInfo.CustomerID || !userInfo.workflows)) {
            try {
                const result = await getRegion(tokens.username, tokens.password);

                if (result && result.data) {
                    // Update userInfo with data from getRegion
                    userInfo = {
                        ...userInfo,
                        CustomerID: result.data.CustomerID || userInfo.CustomerID,
                        Username: result.data.Username || userInfo.Username,
                        region: result.data.region || userInfo.region,
                        secondary_customerIDs: result.data.secondary_customerIDs || userInfo.secondary_customerIDs,
                        workflows: result.data.workflows || userInfo.workflows,
                        AccountStatus: result.data.AccountStatus || userInfo.AccountStatus,
                        S3Link: result.data.S3Link || userInfo.S3Link
                    };

                    // Update tokens if they were missing
                    tokens = {
                        ...tokens,
                        id_token: tokens.id_token || result.data.IdToken,
                        access_token: tokens.access_token || result.data.AccessToken,
                        refresh_token: tokens.refresh_token || result.data.refreshToken,
                        expires_in: tokens.expires_in || result.data.expiresIn
                    };
                }
            } catch (error) {
                // Continue with what we have
            }
        }

        const customername = userInfo.CustomerID || userInfo.email; // Use CustomerID if available
        const CustomAttributes = await fetchUserCustomAttributes();

        // Set basic auth info from tokens - match exactly what LoginPage.js does
        SS.secureStorage_setItem("region", userInfo.region || 'US');
        SS.secureStorage_setItem("accesstoken", tokens.id_token || '');
        SS.secureStorage_setItem("updatetoken", tokens.access_token || '');
        SS.secureStorage_setItem("username", userInfo.Username || userInfo.email || '');
        SS.secureStorage_setItem("refreshtoken", tokens.refresh_token || '');
        SS.secureStorage_setItem("expiresin", tokens.expires_in || "3600");
        SS.secureStorage_setItem("secondarycustomerid", userInfo.secondary_customerIDs || '');

        SS.secureStorage_setItem('vlomni_user_info', JSON.stringify(userInfo));
        SS.secureStorage_setItem('vlomni_user_status', userInfo.status || 'pending_review');

        SS.secureStorage_setItem('customername', customername || '');
        SS.secureStorage_setItem('CustomerID', userInfo.CustomerIDKey);
        SS.secureStorage_setItem("CompanyID", userInfo.UserID);
        SS.secureStorage_setItem('selected_customername', customername || '');

        if (userInfo.SecondaryCustomerIDs && userInfo.SecondaryCustomerIDs.trim().length > 0) {
            const secondaryIds = customername + "," + userInfo.SecondaryCustomerIDs;
            SS.secureStorage_setItem('secondary_customerids', secondaryIds);
        }

        const apiurl = SS.secureStorage_getItem("apiurl") || defaultEndpoints.apiurl;
        const apigClient = apigClientFactory.newClient(undefined, SS.secureStorage_getItem("idtoken"));

        const paramsv = {
            CustomerID: SS.secureStorage_getItem('customername')
        };
        const accessTokenv = SS.secureStorage_getItem('idtoken');

        const additionalParamsv = {
            headers: {
                "Authorization": `Bearer ${accessTokenv}`
            }
        };
        try {
            const result = await apigClient.dbopsGetWorkflowNamesCustomerIDGet(paramsv, '', additionalParamsv);
            SS.secureStorage_setItem('workflows', JSON.stringify(result.data.records));
        } catch (error) {
            throw error;
        }

        const endPoints = isProd ? config.prodEndPoints : config.devEndPoints;
        const validEndpoints = getValidEndpoints(endPoints, 'us-east-1', isProd);
        SS.secureStorage_setItem("apiurl", validEndpoints.apiurl);
        SS.secureStorage_setItem("bucketurl", validEndpoints.bucketurl);
        SS.secureStorage_setItem("bucketname", validEndpoints.bucketname);

        // if (customername === 'VLOMNI_DEMO') {
        //     const deleteBody = {
        //         CustomerID: SS.secureStorage_getItem("customername"),
        //         "IsRead": 0
        //     };

        //     if (isProd) {
        //         deleteBody['messageids'] = '1670664,1629741,1633795';
        //     }

        //     try {
        //         await apigClient.dbopsBulkupdatewebservicemessagePut('', deleteBody, {
        //             headers: {
        //                 "Authorization": SS.secureStorage_getItem("idtoken")
        //             }
        //         });
        //     } catch (error) {
        //         // Continue even if VLOMNI_DEMO update fails
        //     }
        // }

        const params = {
            tablename: "WebServiceCustomer"
        };

        const accessToken = SS.secureStorage_getItem('idtoken');

        const additionalParams = {
            queryParams: {
                fields: "StoreAdminURL, PaymentGateway, PaymentGatewayID, CompanyID, Status, ID, LongName"
            },
            headers: {
                "Authorization": `Bearer ${accessToken}`
            }
        };
        const userDetails = await apigClient.dbopsGetLatestRecordsTablenameGet(params, '', additionalParams);

        if (userDetails.data) {
            const user = userDetails.data[0];

            // SS.secureStorage_setItem("storeadminurl", user[0] || "null");
            // SS.secureStorage_setItem("PaymentGateway", user[1] || "");
            // SS.secureStorage_setItem("PaymentGatewayID", user[2] || "");
            // SS.secureStorage_setItem("CompanyID", user[3] || "");
            // SS.secureStorage_setItem("EDIstatus", user[4] || "");
            // SS.secureStorage_setItem("CustomerID", user[5] || "");
            // SS.secureStorage_setItem("CompanyName", user[6] || customername);

            SS.secureStorage_setItem("storeadminurl", user['StoreAdminURL'] || "null");
            SS.secureStorage_setItem("PaymentGateway", user['PaymentGateway'] || "");
            SS.secureStorage_setItem("PaymentGatewayID", user['PaymentGatewayID'] || "");
            SS.secureStorage_setItem("CompanyID", user['CompanyID'] || "");
            SS.secureStorage_setItem("EDIstatus", user['Status'] || "");
            SS.secureStorage_setItem("CompanyName", user['LongName'] || customername);
        }

        if (userInfo.workflows) {
            appendReverseKeyValuePairs(userInfo.workflows);

            const workflowValues = Object.values(userInfo.workflows);
            const workflowCategories = workflowValues.map(workflow => {
                if (workflow.includes('PRD')) return 'product';
                if (workflow.includes('ORD')) return 'order';
                if (workflow.includes('INV')) return 'inventory';
                if (workflow.includes('FMT')) return 'fulfillment';
                if (workflow.includes('PRC')) return 'price';
                return 'unknown';
            });

            SS.secureStorage_setItem('workflows names', JSON.stringify(workflowValues));
        }

        const accessRights = await readAccess();
        SS.secureStorage_setItem('access', JSON.stringify(accessRights));
        SS.secureStorage_setItem("database", 'dynamodb');

        SS.secureStorage_setItem('accountstatus', userInfo.AccountStatus || '');

        if (userInfo.S3Link) {
            SS.secureStorage_setItem("userAvatar", userInfo.S3Link);
            SS.secureStorage_setItem("main_user_Avatar", userInfo.S3Link);
        } else {
            SS.secureStorage_setItem("userAvatar", config.default_userAvatar);
            SS.secureStorage_setItem("main_user_Avatar", config.default_userAvatar);
        }

        dropCookie('vlomni-cookie-active', now, config.SESSION_EXPIRESIN);
        dropCookie('vlomni-cookie-loggedin', now, parseInt(SS.secureStorage_getItem('expiresin') || '3600'));

        SS.secureStorage_setItem('hubspot_enabled', 'true');

        try {
            await loadHubSpot();
            await new Promise(resolve => setTimeout(resolve, 1000));
            window.location = "/";
        } catch (error) {
            window.location = "/";
        }

        return true;
    } catch (error) {
        throw error;
    }
}; 